import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { getStorefrontProduct, percentOff, shopPrice } from '@/features/marketplace/api'
import { useCart } from '@/features/marketplace/cart'
import { useStorefrontSettings } from '@/features/marketplace/useStorefront'
import { ProductImage } from '@/routes/storefront/ProductImage'
import { QuantityStepper } from '@/routes/storefront/QuantityStepper'
import { toast } from '@/lib/toast'

/**
 * One product's detail page (M24-02).
 *
 * A 404 here reads the same whether the id never existed or the product was unlisted
 * after being linked from search -- see `marketplace/router.py`'s docstring for why
 * that's deliberate, not a shortcut.
 */
export function StorefrontProduct() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const add = useCart((s) => s.add)
  const inBasket = useCart(
    (s) => s.lines.find((line) => line.product_id === productId)?.quantity ?? 0,
  )
  const [quantity, setQuantity] = useState(1)
  const { money } = useStorefrontSettings()

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['storefront-product', productId],
    queryFn: () => getStorefrontProduct(productId!),
    enabled: !!productId,
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="Product not found">
          {error instanceof ApiError && error.status !== 404
            ? error.message
            : 'This product is not available.'}
        </Alert>
        <Link to="/store" className="inline-block">
          <Button variant="secondary">Back to shop</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Link
        to="/store"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Back to shop
      </Link>

      <div className="grid gap-6 sm:grid-cols-2">
        <Card>
          <div className="aspect-square overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
            <ProductImage src={product.images?.[0]} name={product.name} textClass="text-5xl" />
          </div>
        </Card>

        <div className="space-y-4">
          {product.category_name && (
            <p className="text-sm text-slate-400">{product.category_name}</p>
          )}
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {product.name}
          </h1>
          <p className="flex flex-wrap items-baseline gap-x-3">
            <span className="text-2xl font-semibold text-brand-600 dark:text-brand-400">
              {money(shopPrice(product))}
            </span>
            {product.sale_price != null && (
              <>
                <span className="text-base text-slate-400 line-through">
                  {money(product.price)}
                </span>
                <span className="text-sm font-medium text-success">
                  {percentOff(product)}% off
                </span>
              </>
            )}
          </p>
          {product.description && (
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {product.description}
            </p>
          )}
          <p className="text-xs text-slate-400">
            Sold per {product.unit_of_measure} · {product.code}
          </p>

          <Card>
            <CardBody className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600 dark:text-slate-400">Quantity</span>
                <QuantityStepper value={quantity} onChange={setQuantity} label="Quantity" />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    add(product, quantity)
                    toast.success(`${product.name} added to your basket.`)
                  }}
                >
                  Add to basket
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    add(product, quantity)
                    navigate('/store/cart')
                  }}
                >
                  Buy now
                </Button>
              </div>

              {inBasket > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {inBasket} already in your{' '}
                  <Link
                    to="/store/cart"
                    className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                  >
                    basket
                  </Link>
                  .
                </p>
              )}

              <p className="text-xs text-slate-400">Tax and delivery are calculated at checkout.</p>
            </CardBody>
          </Card>
        </div>
      </div>

      {product.long_description && (
        <Card>
          <CardHeader title="About this product" />
          <CardBody>
            <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {product.long_description}
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
